import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';

// Components
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Billing from './pages/Billing';
import QuickQuote from './pages/QuickQuote';
import SalesBySeller from './pages/SalesBySeller';
import DesignerService from './pages/DesignerService';
import DesignerProductivity from './pages/DesignerProductivity';
import LostOrders from './pages/LostOrders';
import DelayedOrders from './pages/DelayedOrders';
import PriorityControl from './pages/PriorityControl';
import ServiceBoard from './pages/ServiceBoard';
import InternalErrors from './pages/InternalErrors';
import RefundOrders from './pages/RefundOrders';
import VerificarPedido from './pages/VerificarPedido';
import Reposicao from './pages/Reposicao';
import PedidosTrocados from './pages/PedidosTrocados';
import WhatsAppAutomation from './pages/WhatsAppAutomation';
import N8NAutomation from './pages/N8NAutomation';
import SellerInterface from './pages/SellerInterface';
import SellerSales from './pages/SellerSales';
import DesignerInterface from './pages/DesignerInterface';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';

// Create theme - VettorPrint Brand Colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#D4AF37', // Dourado VettorPrint
      light: '#FFD700',
      dark: '#B8960F',
      contrastText: '#1a1a1a',
    },
    secondary: {
      main: '#1a1a1a', // Preto VettorPrint
      light: '#2d2d2d',
      dark: '#000000',
      contrastText: '#D4AF37',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#6b7280',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#1a1a1a',
    },
    h2: {
      fontWeight: 700,
      color: '#1a1a1a',
    },
    h3: {
      fontWeight: 600,
      color: '#1a1a1a',
    },
    h4: {
      fontWeight: 600,
      color: '#1a1a1a',
    },
    h5: {
      fontWeight: 600,
      color: '#1a1a1a',
    },
    h6: {
      fontWeight: 600,
      color: '#1a1a1a',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 20px',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(212, 175, 55, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(212, 175, 55, 0.3)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          backgroundImage: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          borderRight: '1px solid rgba(212, 175, 55, 0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles?: string[] }> = ({ 
  children, 
  allowedRoles 
}) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Dashboard Router - mostra dashboard diferente baseado no role
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();

  // Vendedores são redirecionados para Minhas Vendas
  if (user?.role === 'SELLER') {
    return <Navigate to="/seller-sales" replace />;
  }

  // Designers veem o Dashboard de Produtividade
  if (user?.role === 'DESIGNER') {
    return <DesignerProductivity />;
  }

  // Suporte, Gerência e Produção não têm acesso ao Painel - redirecionam para Atendimento Arte
  if (user?.role === 'SUPPORT' || user?.role === 'MANAGER' || user?.role === 'PRODUCAO') {
    return <Navigate to="/designer-service" replace />;
  }

  // Admin vê o Dashboard administrativo
  return <Dashboard />;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        {/* Dashboard - rota diferente por role */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <DashboardRouter />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/billing" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}>
              <Billing />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/quick-quote" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <QuickQuote />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/sales-by-seller" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <SalesBySeller />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/designer-service" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <DesignerService />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/designer-productivity" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'DESIGNER', 'SUPPORT', 'MANAGER']}>
              <DesignerProductivity />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/lost-orders" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPPORT', 'MANAGER']}>
              <LostOrders />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/delayed-orders" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPPORT', 'MANAGER']}>
              <DelayedOrders />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/priority-control"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <PriorityControl />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/service-board" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <ServiceBoard />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/internal-errors"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPPORT', 'MANAGER']}>
              <InternalErrors />
            </ProtectedRoute>
          }
        />
        <Route
          path="/refund-orders"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPPORT', 'MANAGER']}>
              <RefundOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verificar-pedido"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <VerificarPedido />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reposicao"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <Reposicao />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pedidos-trocados"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <PedidosTrocados />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp-automation"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <WhatsAppAutomation />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/n8n-automation" 
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <N8NAutomation />
            </ProtectedRoute>
          } 
        />

        {/* Seller Routes */}
        <Route 
          path="/seller-interface" 
          element={
            <ProtectedRoute allowedRoles={['SELLER']}>
              <SellerInterface />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/seller-sales" 
          element={
            <ProtectedRoute allowedRoles={['SELLER']}>
              <SellerSales />
            </ProtectedRoute>
          } 
        />

        {/* Designer Routes */}
        <Route
          path="/designer-interface"
          element={
            <ProtectedRoute allowedRoles={['DESIGNER', 'ADMIN', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <DesignerInterface />
            </ProtectedRoute>
          }
        />

        {/* Settings Route - Todos podem abrir; apenas Admin e Gerência veem conteúdo; demais veem "Sem permissão" */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* Notifications Route - todos os usuários autenticados */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT', 'MANAGER', 'PRODUCAO']}>
              <Notifications />
            </ProtectedRoute>
          }
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Box sx={{ minHeight: '100vh' }}>
            <AppRoutes />
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
